import * as Sentry from "@sentry/react";

import axios, { AxiosError } from "axios";
import type {
  ConnectionCredentials,
  ConnectionEvent,
  ConnectionStatus,
  ConfigureStreamRequest,
  StreamConfigPayload,
} from "./types";
import { RetryManager } from "./RetryManager";
import { ActivityMonitor } from "./ActivityMonitor";
import { diagnosticEventLog } from "@/shared/services/diagnostic/DiagnosticEventLog";
import { throughputTracker } from "@/shared/services/diagnostic/ThroughputTracker";
import type { DiagnosticSource } from "@/shared/services/diagnostic/types";


export interface ConnectionManagerOptions {
  url: string;
  connectionName: string;
  /** Callback to configure stream subscriptions on the server. Auth is handled transparently by the caller. */
  configureFn: (request: ConfigureStreamRequest) => Promise<void>;
  connectionAttemptTimeoutMs: number;
  retryEnabled: boolean;
  logger: (...args: unknown[]) => void;

  // Retry + activity config
  reconnectDelay: number;
  fallbackThreshold: number;
  sseStabilityWindowMs: number;
  activityTimeoutMs: number;

  /** Called when connection status changes (notification only — ConnectionManager owns the status) */
  onStatusChange: (status: ConnectionStatus) => void;
  /** Called when a routable event (topic:action message) is received */
  onEvent: (route: string, data: unknown) => void;
  /** Called when retry/activity-timeout threshold reached */
  onFallbackThresholdReached: () => void;
  /** Called when SSE credentials are received — owner should call reconfigure() with current payloads */
  onCredentialsReceived: () => void;

  /** Check if any subscribers exist (guards reconnect) */
  hasSubscribers: () => boolean;
  /** Whether fallback mode is available (guards threshold check) */
  fallbackEnabled: boolean;
}

/**
 * Manages the full SSE connection lifecycle:
 *   Step 1: GET /api/stream → receive connection_id + connection_secret
 *   Step 2: POST /api/stream/configure with credentials + subscription configs
 *
 * Owns the SSE instance, connection credentials, retry logic (RetryManager),
 * activity monitoring (ActivityMonitor), and all connection-local timers.
 * Communicates lifecycle events to the main class via injected callbacks.
 */
export class ConnectionManager {
  // ===========================================
  // Private State
  // ===========================================

  private sse: EventSource | null = null;
  private connectionCredentials: ConnectionCredentials | null = null;
  private configureInProgress = false;
  private configureRetryCount = 0;
  private connectionEventTimeout: ReturnType<typeof setTimeout> | undefined;
  private connectionStartTime: number = 0;
  private connectionAttemptTimer: ReturnType<typeof setTimeout> | undefined;
  private configureRetryTimer: ReturnType<typeof setTimeout> | undefined;
  private intentionalClose = false;
  private isUpdating = false;
  private _status: ConnectionStatus = "inactive";

  /** Stored listener refs for cleanup before closing EventSource */
  private boundConnectionHandler: EventListener | null = null;
  private boundPingHandler: EventListener | null = null;

  // Config
  private readonly url: string;
  private readonly connectionName: string;
  private readonly configureFn: (request: ConfigureStreamRequest) => Promise<void>;
  private readonly connectionAttemptTimeoutMs: number;
  private readonly activityTimeoutMs: number;
  private readonly retryEnabled: boolean;
  private readonly logger: (...args: unknown[]) => void;

  // Lifecycle callbacks
  private readonly onStatusChange: (status: ConnectionStatus) => void;
  private readonly onEvent: (route: string, data: unknown) => void;
  private readonly onFallbackThresholdReached: () => void;
  private readonly onCredentialsReceived: () => void;

  // Internal subsystems
  private readonly retryManager: RetryManager;
  private readonly activityMonitor: ActivityMonitor;

  // ===========================================
  // Constructor
  // ===========================================

  constructor(options: ConnectionManagerOptions) {
    this.url = options.url;
    this.connectionName = options.connectionName;
    this.configureFn = options.configureFn;
    this.connectionAttemptTimeoutMs = options.connectionAttemptTimeoutMs;
    this.activityTimeoutMs = options.activityTimeoutMs;
    this.retryEnabled = options.retryEnabled;
    this.logger = options.logger;

    this.onStatusChange = options.onStatusChange;
    this.onEvent = options.onEvent;
    this.onFallbackThresholdReached = options.onFallbackThresholdReached;
    this.onCredentialsReceived = options.onCredentialsReceived;

    // Initialize internal retry manager
    this.retryManager = new RetryManager({
      connectionName: options.connectionName,
      reconnectDelay: options.reconnectDelay,
      fallbackThreshold: options.fallbackThreshold,
      sseStabilityWindowMs: options.sseStabilityWindowMs,
      logger: options.logger,
      onRetryAttempt: () => this.connect(),
      onThresholdReached: () => this.onFallbackThresholdReached(),
      onNoSubscribers: () => this.setStatus("inactive"),
      hasSubscribers: options.hasSubscribers,
      fallbackEnabled: options.fallbackEnabled,
    });

    // Initialize internal activity monitor
    this.activityMonitor = new ActivityMonitor({
      connectionName: options.connectionName,
      activityTimeoutMs: options.activityTimeoutMs,
      logger: options.logger,
      getStatus: () => this._status,
      onTimeout: () => this.handleActivityTimeout(),
      onForceReconnect: () => this.handleForceReconnect(),
    });
  }

  // ===========================================
  // Public API
  // ===========================================

  /**
   * Initiate the 2-step SSE connection flow.
   * Closes any existing connection before starting.
   */
  connect(): void {
    this.disconnect();
    this.setStatus("pending");
    this.establishConnection();
  }

  /**
   * Re-run Step 2 (POST /configure) with the given config payloads.
   * Called by the owner when subscriptions change or after credentials are received.
   */
  async reconfigure(configs: StreamConfigPayload[]): Promise<void> {
    await this.configureSubscriptions(configs);
  }

  /**
   * Close the SSE connection, suppress error handlers, and clear state.
   */
  disconnect(): void {
    this.intentionalClose = true;
    this.retryManager.clearStabilityTracking();
    this.activityMonitor.stop();
    this.closeConnection();
  }

  /**
   * Full cleanup — disconnect and clear all timers.
   */
  destroy(): void {
    diagnosticEventLog.record(this.diagnosticSource, "info", "connection-teardown", {
      previousStatus: this._status,
    });
    this.disconnect();
    this.retryManager.destroy();
    this.activityMonitor.destroy();
    this.setStatus("inactive");
  }

  /**
   * Cancel any pending retry and reset retry state.
   * Used by PollingFallbackManager when entering polling mode.
   */
  cancelRetry(): void {
    this.retryManager.cancelRetry();
  }

  // ===========================================
  // Diagnostics
  // ===========================================

  /** Returns diagnostic info about current retry/timeout state for Sentry context */
  getDiagnosticState(): Record<string, unknown> {
    return {
      retryCount: this.retryManager.retryCount,
      activityTimeoutCount: this.retryManager.activityTimeoutCount,
      configureRetryCount: this.configureRetryCount,
      status: this._status,
    };
  }

  // ===========================================
  // Status
  // ===========================================

  get status(): ConnectionStatus {
    return this._status;
  }

  /** Set status with dedup — only notifies listeners when status actually changes. */
  setStatus(newStatus: ConnectionStatus): void {
    if (this._status !== newStatus) {
      const oldStatus = this._status;
      this._status = newStatus;
      diagnosticEventLog.record(this.diagnosticSource, "info", "status-change", {
        from: oldStatus,
        to: newStatus,
      });
      this.onStatusChange(newStatus);
    }
  }

  /** Source string used for diagnostic event log entries */
  private get diagnosticSource(): DiagnosticSource {
    return `${this.connectionName.toLowerCase()}:sse`;
  }

  // ===========================================
  // Private - Activity Timeout Handling
  // ===========================================

  /**
   * Handle activity timeout — decide whether to reconnect or switch to polling.
   */
  private handleActivityTimeout(): void {
    diagnosticEventLog.record(this.diagnosticSource, "warn", "activity-timeout", {
      activityTimeoutMs: this.activityTimeoutMs,
    });

    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("sse.connection", this.connectionName);
      scope.setContext("sse_activity_timeout", {
        activityTimeoutMs: this.activityTimeoutMs,
        throughput: throughputTracker.getSnapshots(),
        diagnosticState: this.getDiagnosticState(),
        documentVisibility:
          typeof document !== "undefined"
            ? document.visibilityState
            : "unknown",
      });
      Sentry.captureMessage(
        `SSE activity timeout — no messages or pings for ${this.activityTimeoutMs}ms`
      );
    });

    const thresholdReached =
      this.retryManager.incrementActivityTimeoutCount();
    if (thresholdReached) {
      this.logger(
        `[${this.connectionName}] activityTimeoutCount reached threshold, triggering fallback`
      );
      this.onFallbackThresholdReached();
    } else {
      this.retryManager.cancelRetry();
      this.connect();
    }
  }

  /**
   * Handle a planned force-reconnect (e.g. user returning to a tab that's been
   * hidden longer than the activity timeout window). Unlike a real activity
   * timeout, this is not an error — the connection may simply be stale because
   * the browser throttled it during the hide. Reconnect cleanly without
   * firing a Sentry alert or counting against the activity-timeout threshold.
   */
  private handleForceReconnect(): void {
    diagnosticEventLog.record(this.diagnosticSource, "info", "force-reconnect-long-hide");
    this.retryManager.cancelRetry();
    this.connect();
  }

  // ===========================================
  // Private - Connection Establishment (Step 1)
  // ===========================================

  /**
   * Two-step connection flow:
   * Step 1: GET /api/stream (no auth, no payload) to receive credentials
   * Step 2: POST /api/stream/configure with credentials to configure topics
   */
  private establishConnection(): void {
    this.intentionalClose = false;
    const endpoint = new URL(this.url);

    this.logger(
      `[${this.connectionName}] Creating SSE connection to ${endpoint}`
    );
    this.connectionStartTime = Date.now();
    diagnosticEventLog.record(this.diagnosticSource, "info", "connection-attempt", {
      url: endpoint.toString(),
    });

    this.sse = new EventSource(endpoint.toString());

    this.logger(
      `[${this.connectionName}] SSE instance created, registering listeners...`
    );

    // Listen for connection event with credentials
    this.boundConnectionHandler = ((event: MessageEvent) => {
      const elapsed = Date.now() - this.connectionStartTime;
      this.logger(
        `[${this.connectionName}] ✓ CONNECTION EVENT RECEIVED (via addEventListener) after ${elapsed}ms`,
        "data:",
        event.data?.substring(0, 200)
      );

      // Clear the timeout since we received the event
      clearTimeout(this.connectionEventTimeout);

      const credentials = this.safeParse<ConnectionEvent>(event.data);

      if (!credentials?.connection_id || !credentials?.connection_secret) {
        this.logger(
          `[${this.connectionName}] Invalid credentials received`,
          event.data
        );
        Sentry.withScope((scope) => {
          scope.setTag("sse.connection", this.connectionName);
          scope.setContext("sse_credentials", {
            rawData: event.data?.substring(0, 500),
          });
          Sentry.captureException(
            new Error("SSE connection received invalid credentials")
          );
        });
        this.handleConnectionError();
        return;
      }

      this.connectionCredentials = {
        connection_id: credentials.connection_id,
        connection_secret: credentials.connection_secret,
      };

      // Record activity — this is real SSE data arriving.
      // Ensures the activity monitor is running after a timeout-triggered reconnect,
      // since disconnect() stops it and onmessage fallback path won't fire here.
      this.activityMonitor.recordActivity();

      diagnosticEventLog.record(this.diagnosticSource, "info", "credentials-received", {
        elapsedMs: elapsed,
      });

      this.logger(
        `[${this.connectionName}] Credentials received, notifying owner...`
      );

      this.onCredentialsReceived();
    }) as EventListener;
    this.sse.addEventListener("connection", this.boundConnectionHandler);

    // Setup common event handlers
    this.setupSSEHandlers();

    // Start connection attempt timeout — if onopen never fires (firewall silently drops),
    // treat as a connection failure after the configured timeout
    this.connectionAttemptTimer = setTimeout(() => {
      if (this._status === "pending" && !this.connectionCredentials) {
        this.logger(
          `[${this.connectionName}] Connection attempt timed out after ${this.connectionAttemptTimeoutMs}ms (onopen never fired)`
        );
        this.handleConnectionError();
      }
    }, this.connectionAttemptTimeoutMs);
  }

  // ===========================================
  // Private - SSE Event Handlers
  // ===========================================

  private setupSSEHandlers(): void {
    if (!this.sse) return;

    // Listen for ping events
    this.boundPingHandler = ((event: MessageEvent) => {
      this.logger(`[${this.connectionName}] Ping received`, event.data);
      this.activityMonitor.recordActivity();
      throughputTracker.increment(`${this.connectionName.toLowerCase()}:ping`);
    }) as EventListener;
    this.sse.addEventListener("ping", this.boundPingHandler);

    this.sse.onmessage = (event: MessageEvent) => {
      if (!event.data) return;

      // Log ALL messages for debugging
      const preview = event.data.substring(0, 100);
      this.logger(
        `[${this.connectionName}] ✓ onmessage | hasCredentials=${!!this.connectionCredentials} | data=${preview}`
      );

      // Record activity on any message
      this.activityMonitor.recordActivity();

      // FALLBACK: Check if this is connection event sent via wrong path
      if (!this.connectionCredentials) {
        const possibleConnection = this.safeParse<ConnectionEvent>(event.data);

        if (
          possibleConnection?.connection_id &&
          possibleConnection?.connection_secret
        ) {
          this.logger(
            `[${this.connectionName}] ⚠️  Connection event received via onmessage (not addEventListener) - backend may have event type issue`
          );

          clearTimeout(this.connectionEventTimeout);
          this.connectionCredentials = {
            connection_id: possibleConnection.connection_id,
            connection_secret: possibleConnection.connection_secret,
          };

          this.logger(
            `[${this.connectionName}] Credentials extracted, notifying owner...`
          );
          this.onCredentialsReceived();
          return; // Don't process as regular message
        }
      }

      // Only mark active if we have credentials (prevents false "active" state)
      if (this._status !== "active") {
        if (this.connectionCredentials) {
          this.setStatus("active");
          // Don't reset retryCount immediately — use stability tracking
          this.retryManager.startStabilityTracking();
        } else {
          this.logger(
            `[${this.connectionName}] Message received but no credentials yet, staying in pending`
          );
        }
      }

      const parsed = this.safeParse<{ route: string; payload: string }>(
        event.data
      );

      if (!parsed) {
        this.logger(
          `[${this.connectionName}] Failed to parse SSE message`,
          event.data
        );
        return;
      }

      const route = parsed.route as string;
      const payload = this.safeParse(parsed.payload) as unknown;

      throughputTracker.increment(this.diagnosticSource);
      this.onEvent(route, payload);
    };

    this.sse.onopen = () => {
      this.logger(
        `[${this.connectionName}] ✓ onopen fired | status=${this._status} | hasCredentials=${!!this.connectionCredentials}`
      );

      // Clear connection attempt timeout — onopen fired successfully
      clearTimeout(this.connectionAttemptTimer);

      if (this.isUpdating) {
        this.logger(`[${this.connectionName}] Connection updated`);
        this.isUpdating = false;
      }

      // Note: Don't reset retryCount here - onopen fires before we know if auth succeeded.
      // retryCount is reset after SSE stays stable for sseStabilityWindowMs.
      this.retryManager.isRetrying = false;

      // Start timeout waiting for connection event (5 seconds)
      this.connectionEventTimeout = setTimeout(() => {
        const elapsed = Date.now() - this.connectionStartTime;
        this.logger(
          `[${this.connectionName}] TIMEOUT after ${elapsed}ms: No connection event received within 5s`
        );
        if (!this.connectionCredentials) {
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTag("sse.connection", this.connectionName);
            scope.setContext("sse_timeout", {
              elapsedMs: elapsed,
              connectionName: this.connectionName,
            });
            Sentry.captureMessage(
              "SSE stream opened but no credentials received within 5s"
            );
          });
          this.handleConnectionError();
        }
      }, 5000);
    };

    this.sse.onerror = () => {
      if (!this.sse) return; // Already cleaned up

      if (this.intentionalClose) {
        this.intentionalClose = false;
        return;
      }

      if (this.retryManager.isRetrying) {
        return;
      }

      this.retryManager.clearStabilityTracking();

      this.logger(
        `[${this.connectionName}] ✗ onerror fired | hasCredentials=${!!this.connectionCredentials} | retryCount=${this.retryManager.retryCount}`
      );
      diagnosticEventLog.record(this.diagnosticSource, "error", "sse-error", {
        retryCount: this.retryManager.retryCount,
        hadCredentials: !!this.connectionCredentials,
      });
      this.setStatus("inactive");

      // Set intentionalClose before close to suppress any further error events
      this.intentionalClose = true;
      this.closeConnection();
      this.activityMonitor.stop();

      if (this.retryEnabled) {
        this.retryManager.scheduleReconnect();
      }
    };
  }

  // ===========================================
  // Private - Configuration (Step 2)
  // ===========================================

  /**
   * Configure subscriptions on the server using POST /api/stream/configure.
   * Called after connection credentials are received, and when subscriptions change.
   */
  private async configureSubscriptions(configs: StreamConfigPayload[]): Promise<void> {
    if (!this.connectionCredentials) {
      this.logger(
        `[${this.connectionName}] Cannot configure without credentials`
      );
      return;
    }

    if (this.configureInProgress) {
      this.logger(
        `[${this.connectionName}] Configuration already in progress`
      );
      return;
    }

    // Check connection is still alive before configuring
    if (!this.sse || this._status === "inactive") {
      this.logger(
        `[${this.connectionName}] Connection dead, aborting configure`
      );
      this.configureInProgress = false; // Reset stuck flag
      return;
    }

    this.configureInProgress = true;
    const configStartTime = Date.now();

    try {
      this.logger(
        `[${this.connectionName}] Configuring ${configs.length} subscription(s)`
      );

      const request: ConfigureStreamRequest = {
        connection_id: this.connectionCredentials.connection_id,
        connection_secret: this.connectionCredentials.connection_secret,
        configs,
      };

      // Send configuration request (auth handled transparently by the configure callback)
      await this.configureFn(request);

      const configDuration = Date.now() - configStartTime;
      diagnosticEventLog.record(this.diagnosticSource, "info", "configure-success", {
        topicCount: configs.length,
        topics: configs.map((c) => c.topic),
        durationMs: configDuration,
      });
      this.logger(
        `[${this.connectionName}] Configuration successful (took ${configDuration}ms)`
      );
      this.setStatus("active");
      this.configureRetryCount = 0;
      // Don't reset retryCount immediately — use stability tracking
      this.retryManager.startStabilityTracking();
    } catch (error) {
      const configDuration = Date.now() - configStartTime;
      this.logger(
        `[${this.connectionName}] Configuration failed after ${configDuration}ms:`,
        error
      );
      this.handleConfigureError(error);
    } finally {
      this.configureInProgress = false;
    }
  }

  // ===========================================
  // Private - Error Handling
  // ===========================================

  /**
   * Handle configuration errors.
   * Routes errors based on HTTP status code or error type.
   */
  private handleConfigureError(error: unknown): void {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    diagnosticEventLog.record(this.diagnosticSource, "error", "configure-error", {
      httpStatus: status,
      message: error instanceof Error ? error.message : String(error),
    });
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail?: string }>;

      if (axiosError.response) {
        // Server responded with error status
        const status = axiosError.response.status;
        const errorDetail =
          axiosError.response.data?.detail || axiosError.message;

        switch (status) {
          case 404:
            // Connection not found - credentials expired or server restarted
            this.logger(
              `[${this.connectionName}] Connection not found (404): ${errorDetail}`
            );
            this.connectionCredentials = null;
            this.setStatus("inactive");

            // Use delayed retry instead of immediate reconnect to prevent tight loop
            if (this.retryEnabled) {
              this.logger(
                `[${this.connectionName}] Scheduling reconnect with backoff...`
              );
              this.retryManager.scheduleReconnect();
            }
            break;

          case 401: {
            // Auth failed — the attachRetryOn401 interceptor already attempted one token refresh.
            // Retry only the configure POST (not the full SSE connection) to avoid burning retryCount.
            this.configureRetryCount++;
            this.logger(
              `[${this.connectionName}] Authentication failed (401): ${errorDetail}. configureRetryCount=${this.configureRetryCount}`
            );

            if (this.configureRetryCount <= 2 && this.sse && this.connectionCredentials) {
              // Retry configure only — keep EventSource alive, don't increment retryCount
              this.logger(
                `[${this.connectionName}] Retrying configure in 2s (EventSource still alive)...`
              );
              this.configureRetryTimer = setTimeout(() => {
                if (this.sse && this.connectionCredentials) {
                  this.onCredentialsReceived();
                }
              }, 2000);
            } else {
              // Session is truly dead — give up
              this.logger(
                `[${this.connectionName}] Configure 401 persists after ${this.configureRetryCount} attempts — session expired`
              );
              this.setStatus("inactive");
              Sentry.withScope((scope) => {
                scope.setTag("sse.connection", this.connectionName);
                scope.setContext("sse_configure_error", {
                  status: 401,
                  errorDetail,
                  configureRetryCount: this.configureRetryCount,
                  connectionName: this.connectionName,
                });
                Sentry.captureException(
                  new Error(
                    `SSE configure 401 — auth exhausted: ${this.connectionName}`
                  )
                );
              });
            }
            break;
          }

          case 400:
            // Bad request (unknown topic, invalid filters, etc.)
            this.logger(
              `[${this.connectionName}] Bad request (400): ${errorDetail}`
            );
            Sentry.withScope((scope) => {
              scope.setTag("sse.connection", this.connectionName);
              scope.setContext("sse_configure_error", {
                status: 400,
                errorDetail,
                connectionName: this.connectionName,
              });
              Sentry.captureException(
                new Error(`SSE configure bad request (400): ${errorDetail}`, {
                  cause: error as Error,
                })
              );
            });
            this.setStatus("inactive");
            // Don't retry bad requests - configuration issue needs to be fixed
            break;

          default:
            // Other HTTP errors (5xx, etc.) - retry with backoff
            this.logger(
              `[${this.connectionName}] Configuration failed (${status}): ${errorDetail}`
            );
            Sentry.withScope((scope) => {
              scope.setLevel("warning");
              scope.setTag("sse.connection", this.connectionName);
              scope.setTag("http.status_code", String(status));
              scope.setContext("sse_configure_error", {
                status,
                errorDetail,
                connectionName: this.connectionName,
              });
              Sentry.captureMessage(
                `SSE configure received server error (${status})`
              );
            });
            this.setStatus("inactive");
            if (this.retryEnabled) {
              this.retryManager.scheduleReconnect();
            }
            break;
        }
      } else if (axiosError.request) {
        // Request made but no response received (network error)
        this.logger(
          `[${this.connectionName}] Configuration network error: ${axiosError.message}`,
          error
        );
        this.setStatus("inactive");
        if (this.retryEnabled) {
          this.retryManager.scheduleReconnect();
        }
      } else {
        // Error setting up the request
        this.logger(
          `[${this.connectionName}] Configuration setup error: ${axiosError.message}`,
          error
        );
        Sentry.withScope((scope) => {
          scope.setTag("sse.connection", this.connectionName);
          Sentry.captureException(
            new Error(`SSE configure setup error: ${axiosError.message}`, {
              cause: error as Error,
            })
          );
        });
        this.setStatus("inactive");
      }
    } else {
      // Non-axios error - retry with backoff
      this.logger(
        `[${this.connectionName}] Configuration error:`,
        error
      );
      Sentry.withScope((scope) => {
        scope.setTag("sse.connection", this.connectionName);
        Sentry.captureException(
          new Error("SSE configure unexpected error", {
            cause: error as Error,
          })
        );
      });
      this.setStatus("inactive");
      if (this.retryEnabled) {
        this.retryManager.scheduleReconnect();
      }
    }
  }

  /**
   * Handle connection establishment errors (Step 1 failures).
   */
  private handleConnectionError(): void {
    this.retryManager.clearStabilityTracking();
    diagnosticEventLog.record(this.diagnosticSource, "error", "connection-failed", {
      elapsedMs: Date.now() - this.connectionStartTime,
    });
    this.logger(
      `[${this.connectionName}] Connection establishment failed`
    );
    this.setStatus("inactive");

    this.intentionalClose = true;
    this.closeConnection();
    this.activityMonitor.stop();

    if (this.retryEnabled) {
      this.retryManager.scheduleReconnect();
    }
  }

  // ===========================================
  // Private - Internal Cleanup
  // ===========================================

  /**
   * Close the SSE instance and clear connection-local timers/state.
   * Does NOT fire lifecycle callbacks — use disconnect() for full teardown.
   */
  private closeConnection(): void {
    clearTimeout(this.connectionEventTimeout);
    clearTimeout(this.connectionAttemptTimer);
    clearTimeout(this.configureRetryTimer);

    if (this.connectionCredentials) {
      this.logger(
        `[${this.connectionName}] Clearing credentials on disconnect`
      );
      this.connectionCredentials = null;
    }

    const sse = this.sse;
    if (sse) {
      this.sse = null; // Prevent re-entrant calls from seeing a stale reference
      // Remove named event listeners before closing to prevent accumulation
      if (this.boundConnectionHandler) {
        sse.removeEventListener("connection", this.boundConnectionHandler);
        this.boundConnectionHandler = null;
      }
      if (this.boundPingHandler) {
        sse.removeEventListener("ping", this.boundPingHandler);
        this.boundPingHandler = null;
      }
      sse.close();
    }
  }

  // ===========================================
  // Private - Utilities
  // ===========================================

  private safeParse<T = unknown>(data: string): T | null {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
}
